import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { RouteBadge, ROUTE_TYPE_META } from "@/lib/routeTypes";
import { Truck, Download, Trash2, Pencil, ArrowUpRight, Users, BatteryCharging, Search } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

function slug(s) { return String(s || "").replace(/\s+/g, "-").toLowerCase(); }

function mondayOf(dateStr) {
  const d = dateStr ? new Date(`${dateStr}T00:00:00Z`) : new Date();
  const wd = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - wd);
  return d.toISOString().slice(0, 10);
}
function currentMonday() { return mondayOf(new Date().toISOString().slice(0, 10)); }

async function downloadVan(vanKey, name) {
  const res = await fetch(`${API}/export/van/${encodeURIComponent(vanKey)}`, { credentials: "include" });
  if (!res.ok) { toast.error("Download failed"); return; }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `van_${name.replace(/\s+/g, "_")}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success(`Downloaded ${name}.xlsx`);
}

function RenameVanDialog({ van, onRenamed }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(van.display_name);
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true);
    try {
      await api.patch(`/vans/${encodeURIComponent(van.van_key)}`, { new_name: name });
      toast.success("Van renamed");
      setOpen(false);
      onRenamed && onRenamed();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`van-rename-${slug(van.display_name)}`} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-[#E2E2E8] rounded-md hover:bg-slate-50 transition-colors duration-150">
          <Pencil className="w-3.5 h-3.5" /> Rename
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Rename van</DialogTitle></DialogHeader>
        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="van-rename-input" />
        <DialogFooter>
          <button onClick={() => setOpen(false)} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
          <button onClick={submit} disabled={busy || !name.trim()} data-testid="van-rename-save" className="inline-flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-md disabled:opacity-60">
            {busy ? "Saving..." : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteVanButton({ van, onDeleted }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  async function doDelete() {
    setBusy(true);
    try {
      const { data } = await api.delete(`/vans/${encodeURIComponent(van.van_key)}`);
      toast.success(`Removed ${van.display_name} · ${data.deleted} entries`);
      qc.invalidateQueries();
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={`van-delete-${slug(van.display_name)}`} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-red-200 text-red-600 hover:bg-red-50 rounded-md transition-colors duration-150">
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete van {van.display_name}?</AlertDialogTitle>
          <AlertDialogDescription>This removes every entry using this van across the last 60 days. Can't be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={busy} onClick={doDelete} className="bg-red-600 hover:bg-red-700">
            {busy ? "Deleting..." : "Delete van"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AllVansView({ vans, onOpen, search, setSearch, weekStart, setWeekStart }) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vans;
    return vans.filter((v) => v.display_name.toLowerCase().includes(q) || v.aliases.some((a) => a.toLowerCase().includes(q)));
  }, [vans, search]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="van-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vans…"
            className="w-full pl-9 pr-3 py-2 border border-[#E2E2E8] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Week</label>
          <input
            type="date"
            data-testid="van-week-input"
            value={weekStart}
            onChange={(e) => setWeekStart(mondayOf(e.target.value))}
            className="px-3 py-2 border border-[#E2E2E8] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono-data"
          />
          <button
            data-testid="van-week-reset"
            onClick={() => setWeekStart(currentMonday())}
            className="px-3 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-md"
          >This week</button>
        </div>
      </div>

      <div className="bg-white border border-[#E2E2E8] rounded-none md:rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 border-b border-[#E2E2E8] bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
          <div className="col-span-4">Van</div>
          <div className="col-span-3">Drivers</div>
          <div className="col-span-1 text-right">Week</div>
          <div className="col-span-1 text-right">60d</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {filtered.length === 0 ? (
          <div className="p-6 text-sm text-slate-500" data-testid="van-empty-state">
            {search ? `No vans match "${search}".` : "No vans yet. Paste your first roster on the Paste tab."}
          </div>
        ) : (
          filtered.map((v) => (
            <div key={v.van_key} className="grid grid-cols-12 items-center px-5 py-3 border-b border-[#E2E2E8] last:border-b-0 hover:bg-slate-50 transition-colors duration-150" data-testid={`van-row-${slug(v.display_name)}`}>
              <button onClick={() => onOpen(v.van_key)} className="col-span-4 text-left text-sm font-medium text-[#2563EB] hover:underline flex items-center gap-2">
                {v.van_key === "ev" ? <BatteryCharging className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
                {v.display_name}
                {v.aliases.length > 1 && <span className="text-xs font-mono-data text-slate-400">({v.aliases.length} aliases)</span>}
                <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
              <div className="col-span-3 text-xs text-slate-600 truncate">{v.drivers.slice(0, 3).join(", ")}{v.drivers.length > 3 ? ` +${v.drivers.length - 3}` : ""}</div>
              <div className="col-span-1 text-right font-mono-data text-sm font-semibold text-[#2563EB]">{v.week_total}</div>
              <div className="col-span-1 text-right font-mono-data text-sm text-slate-500">{v.total}</div>
              <div className="col-span-3 flex items-center justify-end gap-1">
                <button onClick={() => downloadVan(v.van_key, v.display_name)} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:text-[#2563EB] hover:bg-blue-50 rounded-sm">
                  <Download className="w-3.5 h-3.5" /> xlsx
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function VanProfile({ vanKey, onBack }) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["van-detail", vanKey],
    queryFn: async () => (await api.get(`/vans/${encodeURIComponent(vanKey)}`)).data,
  });
  const qc = useQueryClient();

  if (isLoading || !data) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  const meta = (t) => ROUTE_TYPE_META[t] || ROUTE_TYPE_META.other;

  return (
    <div className="space-y-6" data-testid={`van-profile-${vanKey}`}>
      <div className="bg-white border border-[#E2E2E8] rounded-none md:rounded-lg p-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
            {vanKey === "ev" ? <BatteryCharging className="w-7 h-7 text-[#2563EB]" /> : <Truck className="w-7 h-7 text-[#2563EB]" />}
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Van</div>
            <h2 className="font-heading text-3xl font-semibold tracking-tight">{data.display_name}</h2>
            {data.aliases.length > 1 && <div className="text-xs text-slate-500 mt-1 font-mono-data">Aliases: {data.aliases.join(", ")}</div>}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Week Total</div>
              <div className="font-heading text-4xl font-semibold text-[#2563EB] mt-1">{data.week_total}</div>
            </div>
            <button onClick={() => downloadVan(vanKey, data.display_name)} data-testid="van-download" className="inline-flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-md">
              <Download className="w-4 h-4" /> Download
            </button>
            <RenameVanDialog van={{ van_key: vanKey, display_name: data.display_name }} onRenamed={() => { refetch(); qc.invalidateQueries({ queryKey: ["vans"] }); }} />
            <DeleteVanButton van={{ van_key: vanKey, display_name: data.display_name }} onDeleted={onBack} />
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          {["small","big","pickup","walmart","split"].map((k) => {
            const m = meta(k);
            return (
              <div key={k} className="p-4 rounded-sm border" style={{ backgroundColor: m.bg, borderColor: m.border }}>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: m.text }}>{m.label}</div>
                <div className="mt-1 font-heading text-2xl font-semibold" style={{ color: m.text }}>{data.week_counts[k] || 0}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-[#E2E2E8] rounded-none md:rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E2E2E8] flex items-center justify-between">
          <h3 className="font-heading text-lg font-medium flex items-center gap-2"><Users className="w-4 h-4" /> Drivers who used {data.display_name}</h3>
          <span className="text-xs text-slate-500 font-mono-data">{data.drivers.length} drivers</span>
        </div>
        {data.drivers.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No driver history.</div>
        ) : (
          data.drivers.map((d) => (
            <div key={d.driver_name} className="flex items-center justify-between px-5 py-3 border-b border-[#E2E2E8] last:border-b-0 hover:bg-slate-50 transition-colors duration-150">
              <span className="text-sm font-medium">{d.driver_name}</span>
              <span className="text-sm font-mono-data text-slate-700">{d.count} times</span>
            </div>
          ))
        )}
      </div>

      <div className="bg-white border border-[#E2E2E8] rounded-none md:rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E2E2E8]">
          <h3 className="font-heading text-lg font-medium">History (last 60 days)</h3>
        </div>
        <div className="grid grid-cols-12 px-5 py-2 border-b border-[#E2E2E8] bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
          <div className="col-span-3">Date</div>
          <div className="col-span-3">Route</div>
          <div className="col-span-4">Driver</div>
          <div className="col-span-2">Type</div>
        </div>
        {data.history.map((h) => (
          <div key={h.id} className="grid grid-cols-12 px-5 py-2.5 border-b border-[#E2E2E8] last:border-b-0 items-center hover:bg-slate-50 transition-colors duration-150">
            <div className="col-span-3 font-mono-data text-sm text-slate-600">{h.date?.slice(0, 10)}</div>
            <div className="col-span-3 font-mono-data text-sm">{h.route_code}</div>
            <div className="col-span-4 text-sm">{h.driver_name}</div>
            <div className="col-span-2"><RouteBadge type={h.route_type} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Vans() {
  const [search, setSearch] = useState("");
  const [weekStart, setWeekStart] = useState(currentMonday());
  const { data, isLoading } = useQuery({
    queryKey: ["vans", weekStart],
    queryFn: async () => (await api.get(`/vans?week_start=${weekStart}`)).data,
  });
  const vans = useMemo(() => data?.vans || [], [data]);
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("van") || "__all__";
  const setActive = (k) => k === "__all__" ? setSearchParams({}) : setSearchParams({ van: k });

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Registry</div>
      <h1 className="font-heading text-4xl font-semibold tracking-tight mt-1">Vans</h1>
      <p className="text-sm text-slate-500 mt-1">A tab auto-created for every van. EV / Ev / ev are grouped as the same vehicle.</p>

      <div className="mt-8 border-b border-[#E2E2E8] flex overflow-x-auto no-scrollbar">
        <button onClick={() => setActive("__all__")} data-testid="van-tab-all" className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px ${active === "__all__" ? "border-[#2563EB] text-[#2563EB] font-medium" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
          <Truck className="w-4 h-4" /> All Vans <span className="text-xs font-mono-data opacity-70">({vans.length})</span>
        </button>
        {vans.map((v) => (
          <button key={v.van_key} onClick={() => setActive(v.van_key)} data-testid={`van-tab-${slug(v.display_name)}`} className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px whitespace-nowrap ${active === v.van_key ? "border-[#2563EB] text-[#2563EB] font-medium" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
            {v.van_key === "ev" && <BatteryCharging className="w-3.5 h-3.5" />}
            {v.display_name}
            <span className="text-xs font-mono-data px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600">{v.week_total}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 fade-in" key={active}>
        {isLoading ? <div className="p-6 text-sm text-slate-500">Loading…</div> :
          active === "__all__" ? (
            <AllVansView
              vans={vans}
              onOpen={setActive}
              search={search}
              setSearch={setSearch}
              weekStart={weekStart}
              setWeekStart={setWeekStart}
            />
          ) : <VanProfile vanKey={active} onBack={() => setActive("__all__")} />}
      </div>
    </div>
  );
}
